<template>
  <div class="trade-up-items">
    <PagesTradeUpComponentsFilters
      v-model:filters="filters"
      :params="tradeFilters?.data?.searchParams || {}"
      :rarity-prop="selectedItems?.length ? selectedItems[0].rarity : null"
      :stattrack-prop="!!selectedItems?.[0]?.isStattrak"
      @on-change="onFiltersChange"
    />

    <div
      ref="containerRef"
      class="trade-up-items__grid-wrapper"
    >
      <div
        v-if="items?.length"
        class="trade-up-items__grid"
      >
        <LazyPagesTradeUpComponentsCard
          v-for="(item, index) in items"
          :key="item?.market_hash_name"
          :img-lazy="index < 20"
          :item="item"
          class="item"
          @click.prevent.stop="addItem(item)"
        />
      </div>

      <div
        v-if="loading"
        class="loader-wrapper"
      >
        <ui-loader />
      </div>
      <div v-else-if="!items.length">
        <ui-no-result class="mxa" />
      </div>
    </div>
    <div class="trade-up-items_toolbar f gap8 mr64">
      <button
        class="btn btn-primary red"
        @click="clearItems"
      >
        <i class="i-times" />
      </button>
      <button
        class="btn btn-primary btn-violet fg"
        @click="emit('showContract')"
      >
        <template v-if="selectedItems.length < 10">
          <svg
            class="i-pending mr6"
            height="20"
            width="20"
          >
            <use xlink:href="/icons/sprite.svg#i-pending-spinner" />
          </svg>
          {{ $t("selected_items_count", { n: selectedItems.length }) }}
        </template>

        <template v-else>
          {{ $t("trade_up_contract") }}
          <i class="i-arrow rot_l mla" />
        </template>
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { TradeUpFullInfo, TradeUpSelectedItem } from '~/components/Pages/TradeUp/types';
import type { AdvancedSearchBody, AdvancedSearchInputV2 } from '~/graphql/generated/schema-types';
import { onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { EventBus } from '~/composables/bus';
import { useResize } from '~/composables/resize.js';
import { GroupBy, OrderBy } from '~/graphql/generated/schema-types';
import { useTradeUpStore } from '~/store/trade-up';
import { getTradeUpFilters, getTradeUpItemInfo, getTradeUpItems } from '~/utils/api';

const emit = defineEmits(['showContract']);

const currW = useResize();

const tradeUpStore = useTradeUpStore();
const { currentFullObject, possibleItems } = storeToRefs(tradeUpStore);

const selectedItems = computed({
  get: () => tradeUpStore.selectedItems,
  set: (value) => {
    tradeUpStore.selectedItems = value;
  },
});

const { locale } = useI18n();
const page = ref(1);
const maxPages = ref(1);
const items = ref<AdvancedSearchBody[]>([]);
const loading = ref(true);
const containerRef = ref<HTMLElement | null>(null);
const fetchedItems = ref({} as Record<string, TradeUpSelectedItem>);

const filters = ref({
  rarities: [],
  isSouvenir: false,
  isStattrak: false,
  orderBy: OrderBy.HighestPrice,
  collections: [],
  searchText: null,
  groupBy: GroupBy.ItemName,
} as Partial<AdvancedSearchInputV2>);

const { data: tradeFilters, error: filtersError } = await useAsyncData(
  'getTradeUpFilters',
  getTradeUpFilters,
);

if (filtersError.value || tradeFilters?.value?.errors?.length) {
  console.log(
    `TradeUp filters fetch error: ${
      filtersError.value?.message || tradeFilters?.value?.errors?.[0]?.message}`,
  );
}

const { data, error } = await useAsyncData('getTradeUpItems', () =>
  getTradeUpItems(
    {
      ...filters.value,
      collections: filters.value?.collections?.length
        ? filters.value?.collections
        : null,
      rarities: filters.value?.rarities?.length
        ? filters.value?.rarities
        : [
            'Consumer Grade',
            'Industrial Grade',
            'Mil-Spec Grade',
            'Restricted',
            'Classified',
          ],
      categories: ['Equipment', 'Gloves', 'Heavy', 'Pistols', 'Rifles', 'SMGs'],
      exlcudeItemsWithLastRarity: true,
    } as AdvancedSearchInputV2,
    1,
    locale.value,
  ));
loading.value = false;

if (error.value || data?.value?.errors?.length) {
  if (process.client) {
    window.location.reload();
  }
  console.log(
    `TradeUp fetch error: ${error.value || data?.value?.errors?.[0]?.message}`,
  );
  throw createError({
    status: 404,
    statusText:
        `TradeUp fetch error: ${
          error.value || data?.value?.errors?.[0]?.message}`,
  });
}

items.value = data.value?.data?.advancedSearchV2
  ?.result as AdvancedSearchBody[];
maxPages.value = data.value?.data?.advancedSearchV2?.max_pages || 1;

async function fetchItems() {
  loading.value = true;
  if (page.value === 1) {
    items.value = [] as AdvancedSearchBody[];
  }
  try {
    const { data, errors } = await getTradeUpItems(
      {
        ...filters.value,
        collections: filters.value?.collections?.length
          ? filters.value?.collections
          : null,
        rarities: filters.value?.rarities?.length
          ? filters.value?.rarities
          : [
              'Consumer Grade',
              'Industrial Grade',
              'Mil-Spec Grade',
              'Restricted',
              'Classified',
            ],
        categories: [
          'Equipment',
          'Gloves',
          'Heavy',
          'Pistols',
          'Rifles',
          'SMGs',
        ],
        exlcudeItemsWithLastRarity: true,
      } as AdvancedSearchInputV2,
      page.value,
      locale.value,
    );

    if (errors?.length) {
      console.log(`TradeUp fetch error: ${errors[0].message}`);
      throw new Error(`TradeUp fetch error: ${errors[0].message}`);
    }

    items.value
        = page.value > 1
        ? [
            ...items.value,
            ...(data?.advancedSearchV2?.result as AdvancedSearchBody[]),
          ]
        : (data?.advancedSearchV2?.result as AdvancedSearchBody[]);

    maxPages.value = data?.advancedSearchV2?.max_pages || 1;
  } catch {
    // items.value = [] as AdvancedSearchBody[];
    // maxPages.value = 1;
    // page.value = 1;
  } finally {
    loading.value = false;
  }
}

async function onFiltersChange() {
  page.value = 1;
  await fetchItems();
}

async function addItem(item: AdvancedSearchBody) {
  if (selectedItems.value.length < 10) {
    selectedItems.value.push({
      rarity: item.rarity || '',
      is_stattrak: item.is_stattrak || false,
      market_hash_name: item.market_hash_name,
      link: item.link,
      rarity_color: item.rarity_color || '',
      item_min_float: item.item_min_float || 0,
      item_max_float: item.item_max_float || 1,
      exterior: item.exterior || '',
      volume: fetchedItems.value[item.link]?.volume || [],
      markets_info: fetchedItems.value[item.link]?.markets_info || [],
      collection: item.collection || item.crates?.[0]?.market_hash_name || '',
      float: item.item_min_float || 0.01,
      locals: {
        localed_name: item.localed_name || '',
      },
    });

    await fetchItemInfo(item.link);
  }
}

watch(page, async (newPage) => {
  if (newPage > 1) {
    await fetchItems();
  }
});

async function fetchItemInfo(link: string) {
  if (!fetchedItems.value[link]) {
    try {
      const { data, errors } = await getTradeUpItemInfo(link, locale.value);

      if (errors?.length) {
        throw new Error(`TradeUp fetch error: ${errors[0].message}`);
      }

      fetchedItems.value[link] = {
        ...data.itemPageInfo,
        float: data.itemPageInfo.item_min_float,
        rarity: '',
      } as TradeUpSelectedItem;
    } catch (e: any) {
      console.log(`TradeUp fetch error: ${e.message}`);
      EventBus.$emit('notify', {
        success: false,
        msg: `Item info fetch error: ${e.message}`,
      });
    } finally {
      await nextTick(() => {
        selectedItems.value = selectedItems.value.map((i) => {
          if (fetchedItems.value[i.link] && !i.volume?.length) {
            return {
              ...i,
              volume: fetchedItems.value[link]?.volume,
              markets_info: fetchedItems.value[link]?.markets_info,
            };
          }
          return i;
        });
      });
    }
  }
}

watch(selectedItems, (val) => {
  if (currW.value < 990 && val.length === 10)
    emit('showContract');
});

// watch(selectedItems, () => {
//   selectedItems.value = selectedItems.value.map(i => {
//     if (i.markets_info?.length) {
//       return i
//     }
//
//     const itemKey = Object.keys(fetchedItems.value).find(key => key === i.link)
//     if (itemKey && !fetchedItems.value[itemKey]?.volume?.length) {
//       return {
//         ...i,
//         volume: fetchedItems.value[itemKey]?.volume,
//         markets_info: fetchedItems.value[itemKey]?.markets_info
//       }
//     }
//     return i
//   })
// }, {deep: true})

function handleScroll() {
  const container
      = currW.value > 989 ? containerRef.value : document.documentElement;

  if (!container)
    return;

  const scrollTop = container.scrollTop;
  const clientHeight = container.clientHeight;
  const scrollHeight = container.scrollHeight;

  if (
    scrollTop + clientHeight >= scrollHeight - 100
    && !loading.value
    && (page.value === 1 || page.value < maxPages.value)
  ) {
    page.value += 1;
  }
}

function addScrollListener() {
  const container = currW.value > 989 ? containerRef.value : document;
  if (container) {
    container.addEventListener('scroll', handleScroll);
  }
}

function removeScrollListener() {
  const container = currW.value > 989 ? containerRef.value : document;
  if (container) {
    container.removeEventListener('scroll', handleScroll);
  }
}

onMounted(() => {
  addScrollListener();
});

onUnmounted(() => {
  removeScrollListener();
});

watch(currW, () => {
  removeScrollListener();
  addScrollListener();
});

function clearItems() {
  selectedItems.value = [];
  currentFullObject.value = {
    ...currentFullObject.value,
    cost: 0,
    market_hash_name: '',
    profit: 0,
  } as TradeUpFullInfo;
  possibleItems.value = [];
}
</script>

<style lang="scss" scoped>
@use "sass:color";

.trade-up-items {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-grow: 1;

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    gap: 12px;

    @media screen and (max-width: 767px) {
      grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    }

    &-wrapper {
      overflow-y: auto;
      height: calc(100vh - 500px);
      flex-grow: 1;
      @media screen and (max-width: 989px) {
        height: auto;
      }

      &::-webkit-scrollbar {
        width: 8px;
      }

      /* Трек */
      &::-webkit-scrollbar-track {
        border-radius: 10px;
        padding: 4px;
        background: transparent;
      }

      /* Ползунок */
      &::-webkit-scrollbar-thumb {
        background-color: var(--surfaces-outline);
        border-top: 4px solid transparent;
        border-bottom: 4px solid transparent;
        border-right: 0; // Убираем левую границу
        border-left: 4px solid transparent; // Оставляем правую границу для создания эффекта узкого ползунка
        border-radius: 4px;
        background-clip: padding-box; // Отсекает фон по области паддинга, сохраняя эффект отступа
      }

      /* Ползунок при наведении */
      &::-webkit-scrollbar-thumb:hover {
        background-color: color-mix(in srgb, var(--surfaces-outline) 95%, white);
      }
    }
  }

  .loader-wrapper {
    min-height: 60vh;
  }

  &_toolbar {
    position: sticky;
    bottom: 20px;
    @media screen and (min-width: 989px) {
      display: none;
    }
  }
}
</style>
